import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { unifiedAuth } from '../../api/authClient'
import { companyAuth } from '../../api/company/client'

const customerSchema = z.object({
  email: z.string().email('auth.register.validation.invalidEmail'),
  password: z.string().min(8, 'auth.register.validation.passwordMin'),
})

const companySchema = customerSchema.extend({
  company_name: z.string().min(1, 'auth.register.validation.companyNameRequired'),
  tin: z
    .string()
    .length(13, 'auth.register.validation.jibLength')
    .regex(/^\d+$/, 'auth.register.validation.jibDigits'),
  business_unit: z.string().optional(),
  address: z.string().optional(),
})

type CustomerFormData = z.infer<typeof customerSchema>
type CompanyFormData = z.infer<typeof companySchema>

export interface UnifiedRegisterFormProps {
  onClose?: () => void
  /** When in modal, switch to login modal */
  onOpenLogin?: () => void
  /** When provided, call after successful registration (e.g. stay in payment modal and switch to login). */
  onSuccess?: () => void
  embedded?: boolean
  /** Default "register as company" to true (e.g. on /company/register page) */
  defaultRegisterAsCompany?: boolean
}

export default function UnifiedRegisterForm({
  onClose,
  onOpenLogin,
  onSuccess,
  embedded,
  defaultRegisterAsCompany = false,
}: UnifiedRegisterFormProps) {
  const { t } = useTranslation('portal')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [registerAsCompany, setRegisterAsCompany] = useState(defaultRegisterAsCompany)
  const [pendingVerification, setPendingVerification] = useState(false)
  const [verificationEmail, setVerificationEmail] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpVerifying, setOtpVerifying] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  const customerForm = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
  })
  const companyForm = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
  })

  const isLight = embedded
  const inputClass = isLight
    ? 'w-full rounded-lg bg-white border border-slate-300 px-3 py-2 text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:outline-none'
    : 'w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-slate-200 placeholder-slate-500 focus:border-emerald-500 focus:outline-none'
  const labelClass = isLight
    ? 'block text-sm font-medium text-slate-600 mb-1'
    : 'block text-sm font-medium text-slate-400 mb-1'
  const linkClass = isLight ? 'text-blue-600 hover:underline' : 'text-emerald-500 hover:underline'

  const translateMsg = (message: string | undefined) => {
    if (!message) return ''
    return t(message) !== message ? t(message) : message
  }

  const onCustomerSubmit = async (data: CustomerFormData) => {
    setError(null)
    try {
      const res = await unifiedAuth.registerCustomer(data.email, data.password)
      if (res.data.requires_verification) {
        setVerificationEmail(data.email.trim().toLowerCase())
        setPendingVerification(true)
        startResendCooldown()
      } else {
        setSuccess(true)
        onSuccess?.()
      }
    } catch (e: unknown) {
      const detail =
        e &&
        typeof e === 'object' &&
        'response' in e &&
        (e.response as { data?: { detail?: string } })?.data?.detail
      setError(detail ? String(detail) : t('auth.register.errorDefault'))
    }
  }

  const startResendCooldown = () => {
    setResendCooldown(60)
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0 }
        return prev - 1
      })
    }, 1000)
  }

  const handleVerifyOtp = async () => {
    setError(null)
    setOtpVerifying(true)
    try {
      await unifiedAuth.verifyEmail(verificationEmail, otpCode)
      setSuccess(true)
      setPendingVerification(false)
      onSuccess?.()
    } catch (e: unknown) {
      const detail =
        e &&
        typeof e === 'object' &&
        'response' in e &&
        (e.response as { data?: { detail?: string } })?.data?.detail
      setError(detail ? String(detail) : t('auth.register.otpInvalid'))
    } finally {
      setOtpVerifying(false)
    }
  }

  const handleResendOtp = async () => {
    setError(null)
    try {
      await unifiedAuth.resendOtp(verificationEmail)
      startResendCooldown()
    } catch (e: unknown) {
      const detail =
        e &&
        typeof e === 'object' &&
        'response' in e &&
        (e.response as { data?: { detail?: string } })?.data?.detail
      setError(detail ? String(detail) : 'Failed to resend code')
    }
  }

  const onCompanySubmit = async (data: CompanyFormData) => {
    setError(null)
    try {
      await companyAuth.register({
        email: data.email,
        password: data.password,
        company_name: data.company_name,
        tin: data.tin.replace(/\s/g, ''),
        business_unit: data.business_unit || undefined,
        address: data.address || undefined,
      })
      setSuccess(true)
      onSuccess?.()
    } catch (e: unknown) {
      const detail =
        e &&
        typeof e === 'object' &&
        'response' in e &&
        (e.response as { data?: { detail?: string } })?.data?.detail
      setError(detail ? String(detail) : t('auth.register.errorDefault'))
    }
  }

  if (pendingVerification) {
    return (
      <div
        className={
          embedded
            ? 'rounded-xl border border-slate-200 bg-slate-50 p-6'
            : 'w-full max-w-sm rounded-xl bg-slate-900 border border-slate-800 p-6 shadow-xl'
        }
      >
        <h2 className={`text-xl font-semibold mb-2 text-center ${embedded ? 'text-slate-900' : 'text-white'}`}>
          {t('auth.register.otpTitle')}
        </h2>
        <p className={`text-sm mb-4 text-center ${embedded ? 'text-slate-600' : 'text-slate-400'}`}>
          {t('auth.register.otpSentTo')} <strong>{verificationEmail}</strong>
        </p>
        {error && <p className="text-sm text-red-500 mb-3 text-center">{error}</p>}
        <div className="space-y-3">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className={`${inputClass} text-center text-2xl tracking-[0.5em] font-mono`}
            autoFocus
          />
          <button
            type="button"
            onClick={handleVerifyOtp}
            disabled={otpCode.length !== 6 || otpVerifying}
            className={`w-full rounded-lg font-medium py-2 px-4 transition-colors disabled:opacity-50 ${
              isLight ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
            }`}
          >
            {otpVerifying ? '...' : t('auth.register.otpVerify')}
          </button>
          <p className={`text-xs text-center ${embedded ? 'text-slate-500' : 'text-slate-500'}`}>
            {resendCooldown > 0 ? (
              <span>{t('auth.register.otpResendIn')} {resendCooldown}s</span>
            ) : (
              <button type="button" onClick={handleResendOtp} className={linkClass}>
                {t('auth.register.otpResend')}
              </button>
            )}
          </p>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div
        className={
          embedded
            ? 'rounded-xl border border-slate-200 bg-slate-50 p-6 text-center'
            : 'w-full max-w-sm rounded-xl bg-slate-900 border border-slate-800 p-6 shadow-xl text-center'
        }
        data-testid="unified-register-success"
      >
        <h2 className={`text-xl font-semibold mb-2 ${embedded ? 'text-slate-900' : 'text-white'}`}>
          {t('auth.register.successTitle')}
        </h2>
        <p className={`text-sm mb-4 ${embedded ? 'text-slate-600' : 'text-slate-400'}`}>
          {t('auth.register.successMessage')}
        </p>
        <p className={`text-xs ${embedded ? 'text-slate-500' : 'text-slate-500'}`}>
          {t('auth.register.goToLogin')}{' '}
          {onOpenLogin ? (
            <button type="button" onClick={() => { onClose?.(); onOpenLogin(); }} className={linkClass}>
              {t('auth.register.signIn')}
            </button>
          ) : (
            <Link to="/company/login" onClick={onClose} className={linkClass}>{t('auth.register.signIn')}</Link>
          )}
          .
        </p>
      </div>
    )
  }

  return (
    <div
      className={embedded ? undefined : 'w-full max-w-sm rounded-xl bg-slate-900 border border-slate-800 p-6 shadow-xl'}
      data-testid="unified-register-form"
    >
      {!embedded && (
        <>
          <h1 className={`text-xl font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`}>
            {t('auth.register.title')}
          </h1>
          <p className={`text-sm mb-6 ${isLight ? 'text-slate-600' : 'text-slate-500'}`}>
            {t('auth.register.subtitle')}
          </p>
        </>
      )}
      {embedded && (
        <p className={`text-sm mb-4 ${isLight ? 'text-slate-600' : 'text-slate-500'}`}>
          {t('auth.register.subtitle')}
        </p>
      )}

      {/* Register as company switch */}
      <div className="mb-4 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
        <span className={`text-sm font-medium ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>
          {t('auth.register.registerAsCompany')}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={registerAsCompany}
          onClick={() => setRegisterAsCompany((v) => !v)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
            registerAsCompany ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
              registerAsCompany ? 'translate-x-5' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {!registerAsCompany ? (
        <form onSubmit={customerForm.handleSubmit(onCustomerSubmit)} className="space-y-4">
          <div>
            <label className={labelClass}>{t('auth.register.email')}</label>
            <input
              {...customerForm.register('email')}
              type="email"
              className={inputClass}
              placeholder={t('auth.register.placeholderEmail')}
            />
            {customerForm.formState.errors.email && (
              <p className="mt-1 text-sm text-red-500">{translateMsg(customerForm.formState.errors.email.message)}</p>
            )}
          </div>
          <div>
            <label className={labelClass}>{t('auth.register.password')}</label>
            <input
              {...customerForm.register('password')}
              type="password"
              className={inputClass}
              placeholder={t('auth.register.placeholderPassword')}
            />
            {customerForm.formState.errors.password && (
              <p className="mt-1 text-sm text-red-500">{translateMsg(customerForm.formState.errors.password.message)}</p>
            )}
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <button
            type="submit"
            className={`w-full rounded-lg font-medium py-2 px-4 transition-colors ${
              isLight ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
            }`}
          >
            {t('auth.register.registerButton')}
          </button>
        </form>
      ) : (
        <form onSubmit={companyForm.handleSubmit(onCompanySubmit)} className="space-y-4">
          <div>
            <label className={labelClass}>{t('auth.register.email')}</label>
            <input
              {...companyForm.register('email')}
              type="email"
              className={inputClass}
              placeholder={t('auth.register.placeholderEmail')}
            />
            {companyForm.formState.errors.email && (
              <p className="mt-1 text-sm text-red-500">{translateMsg(companyForm.formState.errors.email.message)}</p>
            )}
          </div>
          <div>
            <label className={labelClass}>{t('auth.register.password')}</label>
            <input
              {...companyForm.register('password')}
              type="password"
              className={inputClass}
              placeholder={t('auth.register.placeholderPassword')}
            />
            {companyForm.formState.errors.password && (
              <p className="mt-1 text-sm text-red-500">{translateMsg(companyForm.formState.errors.password.message)}</p>
            )}
          </div>
          <div>
            <label className={labelClass}>{t('auth.register.companyName')}</label>
            <input
              {...companyForm.register('company_name')}
              type="text"
              className={inputClass}
              placeholder={t('auth.register.placeholderCompanyName')}
            />
            {companyForm.formState.errors.company_name && (
              <p className="mt-1 text-sm text-red-500">{translateMsg(companyForm.formState.errors.company_name.message)}</p>
            )}
          </div>
          <div>
            <label className={labelClass}>{t('auth.register.jib')}</label>
            <input
              {...companyForm.register('tin')}
              type="text"
              inputMode="numeric"
              maxLength={13}
              className={inputClass}
              placeholder={t('auth.register.placeholderJib')}
            />
            {companyForm.formState.errors.tin && (
              <p className="mt-1 text-sm text-red-500">{translateMsg(companyForm.formState.errors.tin.message)}</p>
            )}
          </div>
          <div>
            <label className={labelClass}>{t('auth.register.businessUnit')}</label>
            <input
              {...companyForm.register('business_unit')}
              type="text"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('auth.register.address')}</label>
            <input {...companyForm.register('address')} type="text" className={inputClass} />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <button
            type="submit"
            className={`w-full rounded-lg font-medium py-2 px-4 transition-colors ${
              isLight ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
            }`}
          >
            {t('auth.register.registerButton')}
          </button>
        </form>
      )}

      <p className={`mt-4 text-center text-sm ${isLight ? 'text-slate-600' : 'text-slate-500'}`}>
        {t('auth.register.alreadyHaveAccount')}{' '}
        {onOpenLogin ? (
          <button type="button" onClick={() => { onClose?.(); onOpenLogin(); }} className={linkClass}>
            {t('auth.register.signIn')}
          </button>
        ) : (
          <Link to="/company/login" onClick={onClose} className={linkClass}>{t('auth.register.signIn')}</Link>
        )}
      </p>
    </div>
  )
}
